import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';
import { FRECUENCIA_GLOBAL_OPTIONS, labelFrecuencia } from './informeIvaFrequencyOptions';

const DIAS = [
  { v: 0, label: 'Dom' },
  { v: 1, label: 'Lun' },
  { v: 2, label: 'Mar' },
  { v: 3, label: 'Mié' },
  { v: 4, label: 'Jue' },
  { v: 5, label: 'Vie' },
  { v: 6, label: 'Sáb' }
];

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-slate-800">{label}</span>
        {hint && <span className="text-[11px] text-slate-400 text-right max-w-[55%]">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function inputCls() {
  return 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30';
}

export default function InformeIvaAdministracionPage() {
  const [cfg, setCfg] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const patch = (partial) => {
    setCfg((c) => ({ ...c, ...partial }));
    setDirty(true);
  };

  const loadCfg = useCallback(async () => {
    const { data } = await api.get('/informes-iva/config/global');
    setCfg(data);
    setDirty(false);
  }, []);

  const loadResumen = useCallback(async () => {
    const { data } = await api.get('/informes-iva/administracion/resumen');
    setResumen(data);
  }, []);

  useEffect(() => {
    Promise.all([loadCfg(), loadResumen()]).catch((e) => toast.error(e.response?.data?.error || e.message));
  }, [loadCfg, loadResumen]);

  const saveCfg = async () => {
    if (!cfg) return;
    if (cfg.autoSendGlobalEnabled && !window.confirm('¿Activar envío automático al cliente (AUTO_SEND)?')) return;
    if (cfg.modoOperacion === 'PRODUCCION' && !window.confirm('¿Activar PRODUCCIÓN? Se podrán enviar correos reales a clientes.')) return;
    const ini = Number(cfg.periodoIvaDiaInicio ?? 4);
    const fin = Number(cfg.periodoIvaDiaCierre ?? 18);
    if (fin <= ini) {
      toast.error('El día final debe ser mayor que el día de inicio');
      return;
    }
    setBusy(true);
    try {
      await api.put('/informes-iva/config/global', {
        modoOperacion: cfg.modoOperacion || 'PRUEBA',
        frecuenciaGlobalInformes: cfg.frecuenciaGlobalInformes || 'SEMANAL',
        digestMode: cfg.digestMode,
        digestPorContadorEnabled: cfg.digestPorContadorEnabled,
        digestDestinoPruebaEmail: cfg.digestDestinoPruebaEmail,
        digestScheduleEnabled: cfg.digestScheduleEnabled,
        digestCronDayOfWeek: cfg.digestCronDayOfWeek,
        digestCronHour: cfg.digestCronHour,
        digestCronMinute: cfg.digestCronMinute,
        scheduledCronHour: cfg.scheduledCronHour,
        scheduledCronMinute: cfg.scheduledCronMinute,
        scheduledMaxEjecucionesMes: cfg.scheduledMaxEjecucionesMes,
        periodoIvaDiaInicio: cfg.periodoIvaDiaInicio,
        periodoIvaDiaCierre: cfg.periodoIvaDiaCierre,
        persistInformeFiles: cfg.persistInformeFiles,
        emailOficina: cfg.emailOficina,
        frescuraHoras: cfg.frescuraHoras,
        timezone: cfg.timezone,
        retencionDias: cfg.retencionDias,
        autoSyncOnRun: cfg.autoSyncOnRun,
        syncF29OnRun: cfg.syncF29OnRun,
        scheduledRunsEnabled: cfg.scheduledRunsEnabled,
        autoSendGlobalEnabled: cfg.autoSendGlobalEnabled,
        permitirRemanenteCeroAsumido: cfg.permitirRemanenteCeroAsumido
      });
      toast.success('Guardado');
      setDirty(false);
      await loadResumen();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const statusLine = useMemo(() => {
    if (!cfg) return '';
    const prueba = (cfg.modoOperacion || 'PRUEBA') !== 'PRODUCCION';
    const auto = cfg.scheduledRunsEnabled ? 'automáticos ON' : 'automáticos OFF';
    const freq = labelFrecuencia(cfg.frecuenciaGlobalInformes || 'SEMANAL');
    return `${prueba ? 'Piloto' : 'Producción'} · ${auto} · ${freq}`;
  }, [cfg]);

  if (!cfg) {
    return <div className="max-w-2xl mx-auto p-10 text-slate-500 text-sm">Cargando…</div>;
  }

  const modoPrueba = (cfg.modoOperacion || 'PRUEBA') !== 'PRODUCCION';
  const ventana = resumen?.ventanaPeriodo;
  const digestHora = `${String(cfg.digestCronHour ?? 8).padStart(2, '0')}:${String(cfg.digestCronMinute ?? 0).padStart(2, '0')}`;
  const autoHora = `${String(cfg.scheduledCronHour ?? 6).padStart(2, '0')}:${String(cfg.scheduledCronMinute ?? 0).padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/informes-iva" className="text-sm text-indigo-600 hover:underline">
          ← Panel del mes
        </Link>
        <h1 className="text-xl font-bold text-slate-900 mt-3">Configuración Informes IVA</h1>
        <p className="text-sm text-slate-500 mt-1">{statusLine}</p>
        {ventana?.mensaje && <p className="text-xs text-slate-600 mt-2 leading-relaxed">{ventana.mensaje}</p>}

        <div className="mt-8 space-y-6">
          {/* —— Ambiente —— */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <h2 className="text-sm font-semibold text-slate-900">Ambiente</h2>
            <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl">
              <button
                type="button"
                onClick={() => patch({ modoOperacion: 'PRUEBA' })}
                className={`py-2.5 text-sm font-medium rounded-lg transition-colors ${modoPrueba ? 'bg-white shadow text-amber-900' : 'text-slate-600'}`}
              >
                Piloto
              </button>
              <button
                type="button"
                onClick={() => patch({ modoOperacion: 'PRODUCCION' })}
                className={`py-2.5 text-sm font-medium rounded-lg transition-colors ${!modoPrueba ? 'bg-white shadow text-emerald-900' : 'text-slate-600'}`}
              >
                Producción
              </button>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {modoPrueba
                ? 'Los informes se calculan y usted los revisa; no se envían correos al cliente.'
                : 'Puede enviar informes por correo al cliente (según aprobación en el panel).'}
            </p>
          </div>

          {/* —— Informes automáticos —— */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-5">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Informes automáticos</h2>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Baja datos del SII y recalcula el IVA por empresa. No es diario para todas: solo cuando corresponde según la frecuencia.
                Override por empresa en el panel → Cliente.
              </p>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded"
                checked={Boolean(cfg.scheduledRunsEnabled)}
                onChange={(e) => patch({ scheduledRunsEnabled: e.target.checked })}
              />
              <span className="text-sm text-slate-800">Encender informes automáticos</span>
            </label>

            <Field label="Frecuencia (predeterminada)" hint="Si la empresa usa «global»">
              <select className={inputCls()} value={cfg.frecuenciaGlobalInformes || 'SEMANAL'} onChange={(e) => patch({ frecuenciaGlobalInformes: e.target.value })}>
                {FRECUENCIA_GLOBAL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Revisar empresas a las" hint="Una vez al día">
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={0}
                    max={23}
                    className={inputCls()}
                    value={cfg.scheduledCronHour ?? 6}
                    onChange={(e) => patch({ scheduledCronHour: Number(e.target.value) })}
                  />
                  <input
                    type="number"
                    min={0}
                    max={59}
                    className={inputCls()}
                    value={cfg.scheduledCronMinute ?? 0}
                    onChange={(e) => patch({ scheduledCronMinute: Number(e.target.value) })}
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Hora y minuto ({autoHora})</p>
              </Field>
              <Field label="Tope por empresa / mes" hint="Evita repeticiones">
                <input
                  type="number"
                  min={1}
                  max={31}
                  className={inputCls()}
                  value={cfg.scheduledMaxEjecucionesMes ?? 8}
                  onChange={(e) => patch({ scheduledMaxEjecucionesMes: Number(e.target.value) })}
                />
              </Field>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-medium text-slate-700 mb-3">Calendario del mes (Chile)</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Empezar el día" hint="Antes: no hay automáticos">
                  <input type="number" min={1} max={28} className={inputCls()} value={cfg.periodoIvaDiaInicio ?? 4} onChange={(e) => patch({ periodoIvaDiaInicio: Number(e.target.value) })} />
                </Field>
                <Field label="Cambiar mes tributario el día" hint="Antes: mes anterior">
                  <input type="number" min={2} max={28} className={inputCls()} value={cfg.periodoIvaDiaCierre ?? 18} onChange={(e) => patch({ periodoIvaDiaCierre: Number(e.target.value) })} />
                </Field>
              </div>
            </div>

            <button
              type="button"
              disabled={busy}
              className="text-xs text-indigo-600 underline disabled:opacity-50"
              onClick={async () => {
                if (!window.confirm('¿Encolar ahora las empresas que tocan?')) return;
                setBusy(true);
                try {
                  const { data } = await api.post('/informes-iva/jobs/scheduled-run');
                  if (data.skipped) toast.message(`Sin cola: ${data.skipped}`);
                  else toast.success(`${data.enqueued ?? 0} en cola`);
                } catch (e) {
                  toast.error(e.response?.data?.error || e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Probar ahora (manual)
            </button>
          </div>

          {/* —— Correo resumen: colapsado —— */}
          <details className="bg-white rounded-2xl border border-slate-200 shadow-sm group">
            <summary className="cursor-pointer list-none p-5 flex items-center justify-between gap-2">
              <div>
                <span className="text-sm font-semibold text-slate-900">Correo resumen semanal</span>
                <p className="text-xs text-slate-500 mt-0.5">Opcional · lista de empresas a revisar (no es el informe al cliente)</p>
              </div>
              <span className="text-slate-400 text-xs group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <div className="px-5 pb-5 space-y-4 border-t border-slate-100 pt-4">
              <label className="flex items-center gap-3">
                <input type="checkbox" className="w-4 h-4" checked={cfg.digestScheduleEnabled !== false} onChange={(e) => patch({ digestScheduleEnabled: e.target.checked })} />
                <span className="text-sm">Programar envío automático</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                <select className={inputCls()} value={Number(cfg.digestCronDayOfWeek ?? 1)} onChange={(e) => patch({ digestCronDayOfWeek: Number(e.target.value) })}>
                  {DIAS.map((d) => (
                    <option key={d.v} value={d.v}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <input type="number" min={0} max={23} className={inputCls()} value={cfg.digestCronHour ?? 8} onChange={(e) => patch({ digestCronHour: Number(e.target.value) })} />
                <input type="number" min={0} max={59} className={inputCls()} value={cfg.digestCronMinute ?? 0} onChange={(e) => patch({ digestCronMinute: Number(e.target.value) })} />
              </div>
              <p className="text-[11px] text-slate-400">Día · hora · min ({digestHora})</p>

              {modoPrueba ? (
                <Field label="En piloto, enviar a">
                  <input type="email" className={inputCls()} placeholder={resumen?.digestPruebaTo || 'super admin'} value={cfg.digestDestinoPruebaEmail || ''} onChange={(e) => patch({ digestDestinoPruebaEmail: e.target.value })} />
                </Field>
              ) : (
                <>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="w-4 h-4" checked={Boolean(cfg.digestPorContadorEnabled)} onChange={(e) => patch({ digestPorContadorEnabled: e.target.checked })} />
                    <span className="text-sm">Enviar a contadores / oficina (no solo prueba)</span>
                  </label>
                  {cfg.digestPorContadorEnabled && (
                    <>
                      <select className={inputCls()} value={cfg.digestMode || 'POR_ASIGNADO'} onChange={(e) => patch({ digestMode: e.target.value })}>
                        <option value="POR_ASIGNADO">Por contador IVA</option>
                        <option value="GLOBAL_OFICINA">Oficina</option>
                        <option value="AMBOS">Ambos</option>
                      </select>
                      <input type="email" className={inputCls()} placeholder="Email oficina" value={cfg.emailOficina || ''} onChange={(e) => patch({ emailOficina: e.target.value })} />
                    </>
                  )}
                </>
              )}

              <button
                type="button"
                disabled={busy}
                className="text-sm px-3 py-2 border rounded-lg"
                onClick={async () => {
                  const now = new Date();
                  if (!window.confirm('¿Enviar correo resumen ahora?')) return;
                  setBusy(true);
                  try {
                    const { data } = await api.post(`/informes-iva/digest/send/${now.getFullYear()}/${now.getMonth() + 1}`);
                    toast.success(`Enviado (${data.sent ?? 0})`);
                  } catch (e) {
                    toast.error(e.response?.data?.error || e.message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Enviar ahora
              </button>
            </div>
          </details>

          {/* —— Técnico —— */}
          <details className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            <summary className="cursor-pointer p-5 text-sm font-semibold text-slate-900">Más opciones</summary>
            <div className="px-5 pb-5 space-y-3 text-sm border-t border-slate-100 pt-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={Boolean(cfg.autoSyncOnRun)} onChange={(e) => patch({ autoSyncOnRun: e.target.checked })} />
                Sync SII al calcular manualmente
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={cfg.syncF29OnRun !== false} onChange={(e) => patch({ syncF29OnRun: e.target.checked })} />
                Incluir F29 en sync
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={Boolean(cfg.persistInformeFiles)} onChange={(e) => patch({ persistInformeFiles: e.target.checked })} />
                Guardar PDF en servidor
              </label>
              <label className="flex items-center gap-2 text-red-800">
                <input type="checkbox" checked={Boolean(cfg.autoSendGlobalEnabled)} onChange={(e) => patch({ autoSendGlobalEnabled: e.target.checked })} />
                AUTO_SEND al cliente
              </label>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Field label="Frescura datos (h)">
                  <input type="number" className={inputCls()} value={cfg.frescuraHoras ?? 48} onChange={(e) => patch({ frescuraHoras: Number(e.target.value) })} />
                </Field>
                <Field label="Zona horaria">
                  <input type="text" className={inputCls()} value={cfg.timezone || 'America/Santiago'} onChange={(e) => patch({ timezone: e.target.value })} />
                </Field>
              </div>
              <Link to="/informes-iva/correo" className="inline-block text-xs text-indigo-600 underline pt-2">
                Plantillas y SMTP → Correo y envíos
              </Link>
            </div>
          </details>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{dirty ? 'Cambios sin guardar' : 'Al día'}</span>
          <button type="button" disabled={busy} onClick={saveCfg} className="px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl disabled:opacity-40">
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
